import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {Box, Typography} from '@mui/material';

import {DepartmentHealthRoleCoverageHealthSkeleton} from './DepartmentHealthRoleCoverageHealthSkeleton';
import {
  departmentHealthSectionCardSx,
  departmentHealthSectionSubtitleSx,
  departmentHealthSectionTitleSx,
} from './departmentHealthSectionStyles';
import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {scorePill} from '../../../../../layouts/theme/designSystem';
import {getLocalisedPropertyValue} from '../../../../../utils/getLocalisedPropertyValue';
import {getScoreLevelColors, scorePillChipSx} from '../../../../../utils/getScoreLevelColors';
import {
  DEPARTMENT_HEALTH_ROLE_UNMET_COMPETENCY_DISPLAY_LIMIT,
  type DepartmentHealthRoleCoverageItem,
} from '../../types/DepartmentHealthTypes';

export interface Props {
  roleCoverage?: DepartmentHealthRoleCoverageItem[];
  loading?: boolean;
  embedded?: boolean;
}

export const DepartmentHealthRoleCoverageHealth = ({roleCoverage, loading = false, embedded = false}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDepartmentHealthPage.roleCoverageHealth'});
  const currentLanguage = useContext(LanguageContext);

  if (loading || !roleCoverage) {
    return <DepartmentHealthRoleCoverageHealthSkeleton embedded={embedded} />;
  }

  const cardSx = embedded ? {...departmentHealthSectionCardSx, mb: 0} : departmentHealthSectionCardSx;

  return (
    <EmpionPaper sx={cardSx} data-testid='department-health-role-coverage-health'>
      <Box component='h2' sx={departmentHealthSectionTitleSx}>
        <Box component='span' sx={{color: 'text.secondary', display: 'flex', alignItems: 'center'}}>
          <LockOutlinedIcon sx={{fontSize: 15}} />
        </Box>
        {t('title')}
      </Box>

      <Typography variant='body2' sx={{...departmentHealthSectionSubtitleSx, mb: 1.5}}>
        {t('subtitle')}
      </Typography>

      {roleCoverage.map((role, index) => {
        const scoreColors =
          role.averageAlignmentScore !== null ? getScoreLevelColors(role.averageAlignmentScore) : null;
        const visibleUnmet = role.unmetCompetencies.slice(0, DEPARTMENT_HEALTH_ROLE_UNMET_COMPETENCY_DISPLAY_LIMIT);

        return (
          <Box
            key={role.targetProfileId || role.roleTitle}
            sx={{
              mb: index < roleCoverage.length - 1 ? 1.5 : 0,
              pb: index < roleCoverage.length - 1 ? 1.5 : 0,
              borderBottom: index < roleCoverage.length - 1 ? '0.5px solid' : 'none',
              borderColor: 'divider',
            }}
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.875, flexWrap: 'wrap'}}>
              <Typography sx={{fontSize: '12.5px', fontWeight: 600}}>{role.roleTitle}</Typography>
              <Typography sx={{fontSize: '11px', color: 'text.secondary'}}>
                {t('profileCount', {count: role.profileCount})}
              </Typography>
              {scoreColors && role.averageAlignmentScore !== null && (
                <Box
                  component='span'
                  sx={{
                    marginLeft: 'auto',
                    display: 'inline-block',
                    fontSize: '11px',
                    fontWeight: 600,
                    px: 0.875,
                    py: 0.25,
                    borderRadius: '6px',
                    bgcolor: scoreColors.bgcolor,
                    color: scoreColors.color,
                    ...scorePillChipSx,
                  }}
                >
                  {role.averageAlignmentScore}%
                </Box>
              )}
            </Box>

            {visibleUnmet.length === 0 ? (
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.625, color: scorePill.high.color}}>
                <CheckCircleOutlineIcon sx={{fontSize: 16}} />
                <Typography sx={{fontSize: '12px'}}>{t('allClear')}</Typography>
              </Box>
            ) : (
              visibleUnmet.map((competency) => {
                const competencyLabel = getLocalisedPropertyValue(competency, currentLanguage);

                return (
                  <Box
                    key={competencyLabel}
                    sx={{display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.625}}
                  >
                    <Box
                      component='span'
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: scorePill.low.strokeColor,
                        flexShrink: 0,
                        mt: 0.625,
                      }}
                    />
                    <Typography sx={{fontSize: '12px', color: 'text.secondary'}}>
                      {competencyLabel}{' '}
                      <Box component='span' sx={{color: scorePill.low.color, fontWeight: 500, fontSize: '11px'}}>
                        {t('nobodyMeets')}
                      </Box>
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
        );
      })}
    </EmpionPaper>
  );
};
